import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSpinner, FaPaperPlane, FaReply, FaTimes, FaSmile, FaFileAlt, FaShieldAlt, FaCircle, FaCrown, FaStar } from 'react-icons/fa';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:4000'
  : 'https://test3-0ar4.onrender.com';

/* ── Level color tiers ── */
const getLevelColor = (level) => {
  const lvl = level || 1;
  if (lvl >= 100) return { bg: 'rgba(255,59,48,0.1)', border: 'var(--danger, #ff3b30)', text: 'var(--danger, #ff3b30)' };
  if (lvl >= 75)  return { bg: 'rgba(255, 183, 3, 0.1)', border: 'var(--accent, #ffb703)', text: 'var(--accent, #ffb703)' };
  if (lvl >= 50)  return { bg: 'rgba(255, 193, 48, 0.1)', border: 'var(--accent-light, #ffc130)', text: 'var(--accent-light, #ffc130)' };
  if (lvl >= 25)  return { bg: 'rgba(90,200,250,0.1)', border: 'var(--info, #5ac8fa)', text: 'var(--info, #5ac8fa)' };
  return { bg: 'rgba(138,138,165,0.1)', border: 'var(--text-muted, #8a8aa5)', text: 'var(--text-muted, #8a8aa5)' };
};

const handleDelete = async (msg) => {
  if (!window.confirm('Delete this message?')) return;
  try {
    const token = localStorage.getItem('token');
    await fetch(`${BACKEND_URL}/api/admin/chat/delete/${msg.id || msg._id}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) { console.error(e); }
};

/* ── Timestamp formatter ── */
const formatTime = (msg) => {
  if (msg.time) return msg.time;
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ── Single message card ──
function MessageCard({ msg, user, onReply }) {
  const [hovered, setHovered] = useState(false);
  const isAdmin   = msg.isAdmin || msg.roles?.includes('admin');
  const canDelete = user?.isAdmin || user?.roles?.includes('admin');
  const lvlStyle  = getLevelColor(msg.level);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        padding: '12px 16px',
        transition: 'background 0.2s',
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent',
      }}
    >
      {/* Reply Line */}
      {msg.replyTo && (
        <div style={{
          position: 'absolute',
          left: '28px',
          top: '-10px',
          width: '20px',
          height: '30px',
          borderLeft: '2px solid #2d3748',
          borderTop: '2px solid #2d3748',
          borderTopLeftRadius: '12px',
          zIndex: 0
        }} />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', position: 'relative', zIndex: 1 }}>
        {/* Avatar */}
        <div style={{ flexShrink: 0 }}>
          <div 
            onClick={() => window.showUserStats?.(msg.user)}
            style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#1a1c22',
                cursor: 'pointer',
                border: '1px solid #2d3748'
            }}
          >
            <img
              src={msg.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${msg.user}&backgroundColor=transparent`}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <div style={{ 
                background: lvlStyle.bg, 
                border: `1px solid ${lvlStyle.border}`,
                color: lvlStyle.text,
                fontSize: '11px',
                fontWeight: 900,
                padding: '2px 6px',
                borderRadius: '4px',
                fontStyle: 'italic'
            }}>
                {msg.level || 1}
            </div>
            <span
              onClick={() => !msg.system && window.showUserStats?.(msg.user)}
              style={{
                fontWeight: 900,
                color: msg.system ? '#ffb703' : '#fff',
                fontSize: '14px',
                cursor: msg.system ? 'default' : 'pointer'
              }}
            >
              {msg.user}
            </span>
            {isAdmin && (
                <FaShieldAlt size={10} color="#ef4444" title="Moderator" />
            )}
            {!msg.system && <FaStar size={10} color="#ffb703" style={{ opacity: 0.6 }} />}
          </div>

          {/* Message text */}
          <div style={{
            color: msg.system ? '#ffb703' : '#9ca3af',
            fontSize: '13px',
            lineHeight: '1.5',
            fontWeight: msg.system ? 800 : 700,
            wordBreak: 'break-word',
          }}>
            {msg.text}
          </div>

          {/* Optional icons (Case, etc.) - Placeholder logic */}
          {msg.text.toLowerCase().includes('case') && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(255, 183, 3, 0.1)', padding: '2px 6px', borderRadius: '4px', marginTop: '6px', fontSize: '11px', fontWeight: 900, color: '#ffb703' }}>
                <FaBoxOpen size={10} /> Case
            </div>
          )}
        </div>

        {/* Hover actions */}
        <AnimatePresence>
          {hovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                display: 'flex', gap: '4px', position: 'absolute', top: '0', right: '0'
              }}
            >
              {user && (
                <button
                  onClick={() => onReply(msg)}
                  style={{
                    background: '#1a1c22', color: '#9ca3af', border: '1px solid #2d3748', cursor: 'pointer',
                    width: '28px', height: '28px', borderRadius: '4px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <FaReply size={10} />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(msg); }}
                  style={{
                    background: '#1a1c22', color: '#ef4444', border: '1px solid #2d3748', cursor: 'pointer',
                    width: '28px', height: '28px', borderRadius: '4px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <FaTimes size={10} />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Compact continuation message (same user, no avatar) ──
function ContinuationMessage({ msg, user, onReply }) {
  const [hovered, setHovered] = useState(false);
  const canDelete = user?.isAdmin || user?.roles?.includes('admin');

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        padding: '2px 16px 2px 64px', /* 36px avatar + 12px gap + 16px padding */
        transition: 'background 0.2s',
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent',
      }}
    >
      <div style={{
        color: msg.system ? '#ffb703' : '#9ca3af',
        fontSize: '13px', lineHeight: '1.5', fontWeight: 700,
        wordBreak: 'break-word',
      }}>
        {msg.text}
      </div>

      {/* Hover actions */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              display: 'flex', gap: '4px', position: 'absolute', top: '0', right: '0', zIndex: 10,
            }}
          >
            {user && (
              <button onClick={() => onReply(msg)} style={{
                background: '#1a1c22', color: '#9ca3af', border: '1px solid #2d3748', cursor: 'pointer',
                width: '24px', height: '24px', borderRadius: '4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <FaReply size={10} />
              </button>
            )}
            {canDelete && (
              <button onClick={(e) => { e.stopPropagation(); handleDelete(msg); }} style={{
                background: '#1a1c22', color: '#ef4444', border: '1px solid #2d3748', cursor: 'pointer',
                width: '24px', height: '24px', borderRadius: '4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <FaTimes size={10} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── ChatMessageList export (with message grouping) ──
export function ChatMessageList({ chatMessages, mutedUsers = [], user, chatEndRef, onReply }) {
  // Filter muted users first
  const filtered = chatMessages?.filter(msg => !mutedUsers.includes?.(msg.user)) || [];

  return (
    <div
      className="chat-messages"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '12px 0',
        overflowY: 'auto',
        flex: 1,
      }}
    >
      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', marginTop: '40px', padding: '0 20px' }}>
          <p style={{ color: '#6b7280', fontSize: '13px', fontWeight: 800 }}>No messages yet. Start the conversation!</p>
        </div>
      )}
      <AnimatePresence initial={false}>
        {filtered.map((msg, i) => {
          const prev = i > 0 ? filtered[i - 1] : null;
          const isGrouped = prev && prev.user === msg.user && !msg.replyTo && !msg.system && !prev.system;

          return (
            <motion.div
              key={msg.id || msg._id || i}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.15 }}
            >
              {isGrouped
                ? <ContinuationMessage msg={msg} user={user} onReply={onReply || (() => {})} />
                : <MessageCard msg={msg} user={user} onReply={onReply || (() => {})} />
              }
            </motion.div>
          );
        })}
      </AnimatePresence>
      <div ref={chatEndRef} />
    </div>
  );
}

// ── ChatInputBar export ──
export function ChatInputBar({ chatInput, setChatInput, onSubmit, sending, user, replyTo, onCancelReply, onRulesClick }) {
  const [showEmojis, setShowEmojis] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const emojiRef = useRef(null);

  const quickEmojis = ['😂','😭','🥺','🤣','❤️','✨','🙏','🔥','😍','💀','👍','💰','🎰','💎','🚀','😎','🤑','💯','⭐','🃏','🎲','👑','💸','🏆'];

  useEffect(() => {
    if (!showEmojis) return;
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmojis(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmojis]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Reply preview */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'rgba(255, 183, 3, 0.05)',
              borderLeft: '3px solid #ffb703',
              borderRadius: '4px',
              padding: '8px 12px',
              marginBottom: '4px'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#ffb703', fontSize: '11px', fontWeight: 900 }}>Replying to {replyTo.user}</div>
                <div style={{ color: '#9ca3af', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{replyTo.text}</div>
              </div>
              <button onClick={onCancelReply} style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer' }}><FaTimes size={10} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form
        onSubmit={e => { e.preventDefault(); if (!sending) onSubmit(); }}
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          background: '#111318',
          padding: '8px 12px',
          borderRadius: '8px',
          border: inputFocused ? '1px solid #ffb703' : '1px solid #1a1c22',
          transition: 'all 0.2s',
        }}
      >
        <input
          type="text"
          placeholder={user ? "Type here..." : "Sign in to chat"}
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          disabled={sending || !user}
          style={{
            flex: 1, background: 'transparent', border: 'none',
            color: '#fff', fontSize: '14px', fontWeight: 700, outline: 'none',
          }}
        />

        <div style={{ position: 'relative', display: 'flex', gap: '12px', alignItems: 'center' }} ref={emojiRef}>
          <button
            type="button"
            onClick={() => setShowEmojis(!showEmojis)}
            style={{ background: 'transparent', color: '#6b7280', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <FaSmile size={18} />
          </button>

          {/* Emoji picker */}
          <AnimatePresence>
            {showEmojis && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                style={{
                  position: 'absolute', bottom: 'calc(100% + 15px)', right: 0,
                  background: '#16191f', border: '1px solid #1a1c22', borderRadius: '12px',
                  padding: '12px', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: '4px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', zIndex: 100, width: '240px',
                }}
              >
                {quickEmojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => { setChatInput(prev => prev + emoji); setShowEmojis(false); }}
                    style={{ background: '#111318', border: '1px solid #1a1c22', fontSize: '18px', cursor: 'pointer', padding: '6px', borderRadius: '6px' }}
                  >
                    {emoji}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            disabled={sending || !chatInput.trim() || !user}
            style={{
              background: 'transparent',
              color: (!chatInput.trim() || !user) ? '#2d3748' : '#ffb703',
              border: 'none',
              cursor: (!chatInput.trim() || !user) ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center'
            }}
          >
            {sending ? <FaSpinner className="fa-spin" size={16} /> : <FaPaperPlane size={16} />}
          </button>
        </div>
      </form>
    </div>
  );
}
